(function () {
  applyPolicyRouteFixes();

  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          viewTransition.skipTransition();
        },
        { once: true }
      );
    });

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

    if (transitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(transitionType);
      sessionStorage.setItem('custom-transition-type', transitionType);
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      sessionStorage.removeItem('custom-transition-type');
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);
    applyPolicyRouteFixes();

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    const customTransitionType = sessionStorage.getItem('custom-transition-type');

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      await viewTransition.finished;

      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');

      idleCallback(() => {
        sessionStorage.removeItem('custom-transition-type');
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  });

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition) {
    return !(viewTransition instanceof ViewTransition) || isLowPowerDevice();
  }

  function applyPolicyRouteFixes() {
    injectTermsPolicyEmbed();
    synchronizeRefundPolicyContent();
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }

  function injectTermsPolicyEmbed() {
    if (!/^\/policies\/terms-of-service\/?$/.test(window.location.pathname)) return;

    const STYLE_ID = 'terms-policy-embed-styles';
    let attempts = 0;

    const ensureStyles = () => {
      if (document.getElementById(STYLE_ID)) return;

      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = `
        .terms-policy-embed {
          display: block;
          width: 100%;
          min-height: 520px;
          margin-top: 2rem;
          border: 0;
        }

        @media screen and (max-width: 749px) {
          .terms-policy-embed {
            min-height: 640px;
          }
        }
      `;

      document.head.appendChild(style);
    };

    const mountEmbed = () => {
      if (document.querySelector('.terms-policy-embed')) return;

      const target =
        document.querySelector('.shopify-policy__body .rte') ||
        document.querySelector('.shopify-policy__body');

      if (!target) {
        if (attempts < 20) {
          attempts += 1;
          window.setTimeout(mountEmbed, 250);
        }
        return;
      }

      ensureStyles();

      const embed = document.createElement('object');
      embed.className = 'terms-policy-embed';
      embed.data = 'https://www.9-bill.com/index/text';
      embed.style.width = '100%';
      embed.setAttribute('aria-label', 'Terms supplemental company information');

      target.appendChild(embed);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', mountEmbed, { once: true });
    } else {
      mountEmbed();
    }
  }

  function synchronizeRefundPolicyContent() {
    if (!/^\/policies\/refund-policy\/?$/.test(window.location.pathname)) return;

    const FINAL_CONTENT = {
      returnAddress: '5625 Challenge Dr, Suite 104, Memphis, TN 38115, United States.',
      processingTime: 'Orders are usually processed within 1-3 business days after the order is placed.',
      cancellationWindow: 'Customers may cancel their order within 1 day after placing the order.',
      cancellationBeforeShipment: 'Orders can be cancelled only before shipment.',
      cancellationRule:
        'If the customer requests cancellation before the order is shipped, and the request is made within the allowed cancellation period stated on the website, the order can be cancelled.',
    };

    let attempts = 0;
    const MAX_ATTEMPTS = 40;
    let isApplying = false;

    const resolveRefundTarget = () => {
      const target = document.querySelector('.shopify-policy__body .rte');
      return target instanceof HTMLElement && target.children.length > 1 ? target : null;
    };

    const hasFinalRefundContent = (target) => {
      const text = normalizeBlockText(target);
      return (
        text.includes(FINAL_CONTENT.returnAddress) &&
        text.includes(FINAL_CONTENT.processingTime) &&
        text.includes(FINAL_CONTENT.cancellationWindow) &&
        text.includes(FINAL_CONTENT.cancellationBeforeShipment) &&
        text.includes(FINAL_CONTENT.cancellationRule)
      );
    };

    const normalizeBlockText = (element) => element?.innerText?.replace(/\s+/g, ' ').trim() || '';

    const findPolicyBlock = (target, heading) =>
      Array.from(target.children).find((child) => child instanceof HTMLElement && normalizeBlockText(child).startsWith(heading));

    const clearNode = (node) => {
      while (node.firstChild) {
        node.removeChild(node.firstChild);
      }
    };

    const buildParagraphContent = (container, heading, lines) => {
      clearNode(container);

      const headingNode = document.createElement('span');
      headingNode.textContent = heading;
      container.appendChild(headingNode);

      lines.forEach((line) => {
        container.appendChild(document.createElement('br'));
        container.appendChild(document.createTextNode(line));
      });
    };

    const buildListContent = (container, heading, items, list) => {
      clearNode(container);

      const headingNode = document.createElement('span');
      headingNode.textContent = heading;
      container.appendChild(headingNode);

      const itemTemplate = list.querySelector('li');
      const itemSpanTemplate = itemTemplate?.querySelector('span');

      clearNode(list);

      items.forEach((item) => {
        const listItem = itemTemplate ? itemTemplate.cloneNode(false) : document.createElement('li');
        const span = itemSpanTemplate ? itemSpanTemplate.cloneNode(false) : document.createElement('span');
        span.textContent = item;
        listItem.appendChild(span);
        list.appendChild(listItem);
      });
    };

    const createTextBlock = (template, heading, lines) => {
      const block = template.cloneNode(true);
      const paragraphs = Array.from(block.querySelectorAll('p'));
      const paragraph = paragraphs[0];

      if (!paragraph) return null;

      paragraphs.slice(1).forEach((node) => node.remove());
      block.querySelectorAll('ul, ol').forEach((node) => node.remove());

      const contentSpan = paragraph.querySelector('span') || paragraph;
      buildParagraphContent(contentSpan, heading, lines);

      return block;
    };

    const createListBlock = (template, heading, items) => {
      const block = template.cloneNode(true);
      const paragraphs = Array.from(block.querySelectorAll('p'));
      const paragraph = paragraphs[0];
      const lists = Array.from(block.querySelectorAll('ul, ol'));
      const list = lists[0];

      if (!paragraph || !list) return null;

      paragraphs.slice(1).forEach((node) => node.remove());
      lists.slice(1).forEach((node) => node.remove());

      const contentSpan = paragraph.querySelector('span') || paragraph;
      buildListContent(contentSpan, heading, items, list);

      return block;
    };

    const mountRefundSections = () => {
      if (isApplying) return;

      const target = resolveRefundTarget();

      if (!target) {
        if (attempts < MAX_ATTEMPTS) {
          attempts += 1;
          window.setTimeout(mountRefundSections, 500);
        }
        return;
      }

      if (hasFinalRefundContent(target)) return;

      const contactBlock = findPolicyBlock(target, 'Contact Info');
      const refundsBlock = findPolicyBlock(target, 'Refunds');
      const listTemplate = findPolicyBlock(target, 'Return steps') || findPolicyBlock(target, 'Non-returnable items');

      if (!contactBlock || !refundsBlock || !listTemplate) {
        if (attempts < MAX_ATTEMPTS) {
          attempts += 1;
          window.setTimeout(mountRefundSections, 500);
        }
        return;
      }

      isApplying = true;

      try {
        ['Return Address', 'Order Processing Time', 'Order Cancellation'].forEach((heading) => {
          const existingBlock = findPolicyBlock(target, heading);
          existingBlock?.remove();
        });

        const returnAddressBlock = createTextBlock(contactBlock, 'Return Address', [
          `Return Address: ${FINAL_CONTENT.returnAddress}`,
        ]);
        const processingBlock = createListBlock(listTemplate, 'Order Processing Time', [FINAL_CONTENT.processingTime]);
        const cancellationBlock = createListBlock(listTemplate, 'Order Cancellation', [
          FINAL_CONTENT.cancellationWindow,
          FINAL_CONTENT.cancellationBeforeShipment,
          FINAL_CONTENT.cancellationRule,
        ]);

        [returnAddressBlock, processingBlock, cancellationBlock]
          .filter(Boolean)
          .forEach((block) => refundsBlock.parentNode.insertBefore(block, refundsBlock));

        if (hasFinalRefundContent(target)) {
          target.dataset.refundPolicySyncApplied = 'true';
        }
      } finally {
        isApplying = false;
      }
    };

    const startObserver = () => {
      if (window.__refundPolicySyncObserverInstalled) return;
      if (!document.body) return;

      const observer = new MutationObserver(() => {
        mountRefundSections();
      });

      observer.observe(document.body, { childList: true, subtree: true });
      window.__refundPolicySyncObserverInstalled = true;
    };

    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          startObserver();
          mountRefundSections();
        },
        { once: true }
      );
    } else {
      startObserver();
      mountRefundSections();
    }

    [1500, 4000, 8000, 12000].forEach((delay) => {
      window.setTimeout(mountRefundSections, delay);
    });
  }
})();
